import { create } from "zustand";
import type { StorageAdapter } from "./storage";

export interface AuthUser {
  id: number;
  username: string;
  division: string;
  divisionName: string;
  accessGroup: string;
  salesRep: string | null;
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setAuth: (token: string, refreshToken: string, user: AuthUser) => void;
  setToken: (token: string, refreshToken?: string) => void;
  clearAuth: () => void;
}

let _storageAdapter: StorageAdapter | null = null;
const STORAGE_KEY = "hops-panel-auth";

/** Call this before using the auth store to enable persistence. */
export function initAuthStorage(adapter: StorageAdapter) {
  _storageAdapter = adapter;
}

export const useAuthStore = create<AuthState>()((set) => ({
  token: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,

  setAuth: (token, refreshToken, user) => {
    set({ token, refreshToken, user, isAuthenticated: true });
    _storageAdapter?.setItem(
      STORAGE_KEY,
      JSON.stringify({ token, refreshToken, user }),
    );
  },

  setToken: (token, refreshToken) => {
    set((state) => ({
      token,
      refreshToken: refreshToken ?? state.refreshToken,
    }));
    const state = useAuthStore.getState();
    _storageAdapter?.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    );
  },

  clearAuth: () => {
    set({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
    });
    _storageAdapter?.removeItem(STORAGE_KEY);
  },
}));

/** Restore persisted auth state from storage adapter. Call on app init. */
export async function restoreAuth(): Promise<boolean> {
  if (!_storageAdapter) return false;
  const raw = await _storageAdapter.getItem(STORAGE_KEY);
  if (!raw) return false;
  try {
    const { token, refreshToken, user } = JSON.parse(raw);
    if (token && user) {
      useAuthStore.getState().setAuth(token, refreshToken, user);
      return true;
    }
  } catch {
    // Corrupt data, ignore
  }
  return false;
}
